import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Button from "../../common/Button";
import classNames from "./TryTranslation.module.css";
import Select from "../../common/Select";
import { getLanguageFlag, getLanguageName } from "../../../types";
import { IoPlay } from "react-icons/io5";
import { useIsMobile } from "../../../hooks/layout";
import RadixDialog from "../../common/dialogs/RadixDialog";

const transcript = [
  // =========================
  // QUESTION (1s to ~17s)
  // =========================
  {
    offset: 1,
    captions: [
      {
        language: "en-US",
        text: "Hydrogen for cars—critics call it the ‘champagne’ of the energy transition,",
      },
      {
        language: "zh-CN",
        text: "用于汽车的氢能——评论家称其为能源转型的「香槟」,",
      },
      {
        language: "de-DE",
        text: "Wasserstoff für Autos – Kritiker bezeichnen Wasserstoff als ‚Champagner‘ der Energiewende,",
      },
    ],
  },
  {
    offset: 7,
    captions: [
      {
        language: "de-DE",
        text: "weil er eben aufgrund des hohen Energieverlustes bei seiner Herstellung eben rar und teuer ist.",
      },
      {
        language: "zh-CN",
        text: "因为在生产过程中能量损耗极大，所以它既昂贵又稀缺。",
      },
      {
        language: "en-US",
        text: "because it’s rare and expensive due to the significant loss of energy in its production.",
      },
    ],
  },
  {
    offset: 13,
    captions: [
      {
        language: "de-DE",
        text: "Werden Ihre Autos in Zukunft diesen ‚Champagner‘ tanken, Herr Diess?",
      },
      {
        language: "zh-CN",
        text: "Diess先生，您的汽车未来会使用这种「香槟」作为燃料吗？",
      },
      {
        language: "en-US",
        text: "Will your cars run on this ‘champagne’ in the future, Mr. Diess? ",
      },
    ],
  },

  // Question finishes around 17 seconds

  // =========================
  // ANSWER (18s to ~55s)
  // =========================

  {
    offset: 18,
    captions: [
      {
        language: "de-DE",
        text: "Aller Voraussicht nach nicht, weil Sie haben den kritischen Punkt eben schon angesprochen.",
      },
      {
        language: "en-US",
        text: "Most likely not, because you already mentioned the critical point.",
      },
      {
        language: "zh-CN",
        text: "很可能不会，因为您已经提到了关键点。",
      },
    ],
  },
  {
    offset: 23,
    captions: [
      {
        language: "de-DE",
        text: "Wasserstoff wird auch, glaube ich, in der Energiewende eine große Rolle spielen,",
      },
      {
        language: "en-US",
        text: "I do believe that hydrogen will play a major role in the energy transition,",
      },
      {
        language: "zh-CN",
        text: "我认为，氢气在能源转型中也扮演着重要角色，",
      },
    ],
  },
  {
    offset: 28,
    captions: [
      {
        language: "de-DE",
        text: "weil man viele Dekarbonisierungsthemen ohne Wasserstoff nicht bewältigen können wird.",
      },
      {
        language: "en-US",
        text: "because many decarbonization issues cannot be tackled without hydrogen.",
      },
      {
        language: "zh-CN",
        text: "因为许多脱碳问题无法在没有氢气的情况下解决。",
      },
    ],
  },
  {
    offset: 35,
    captions: [
      {
        language: "de-DE",
        text: "Wasserstoff ist ein speicherbares Medium,",
      },
      {
        language: "en-US",
        text: "Hydrogen is a storable medium,",
      },
      {
        language: "zh-CN",
        text: "氢气不是一种可储存的介质，",
      },
    ],
  },
  {
    offset: 38,
    captions: [
      {
        language: "de-DE",
        text: "und Wasserstoff wird aber unglaublich wichtig sein bei industriellen Prozessen in der Düngemittelherstellung, in der Stahlherstellung,",
      },
      {
        language: "en-US",
        text: "and hydrogen will be incredibly important in industrial processes such as fertilizer production and steel manufacturing.",
      },
      {
        language: "zh-CN",
        text: "但是氢气在工业过程中将变得非常重要，例如肥料生产和钢铁制造。",
      },
    ],
  },
  {
    offset: 43,
    captions: [
      {
        language: "de-DE",
        text: "da kann man sich eine Dekarbonisierung ohne Wasserstoff nicht vorstellen.",
      },
      {
        language: "en-US",
        text: "One cannot imagine decarbonization without hydrogen.",
      },
      {
        language: "zh-CN",
        text: "无法想象没有氢气的脱碳过程。",
      },
    ],
  },
];

interface AudioSet {
  female: string;
  male: string;
}

type AudioTracks = Record<string, AudioSet>;

const audioTracks: AudioTracks = {
  "en-US": {
    female:
      "https://byrdhouse-public-prod.s3.us-west-1.amazonaws.com/static/home/landing-page-voices/female-en-US.mp3",
    male: "https://byrdhouse-public-prod.s3.us-west-1.amazonaws.com/static/home/landing-page-voices/male-en-US.mp3",
  },
  "zh-CN": {
    female:
      "https://byrdhouse-public-prod.s3.us-west-1.amazonaws.com/static/home/landing-page-voices/female-zh-CN.mp3",
    male: "https://byrdhouse-public-prod.s3.us-west-1.amazonaws.com/static/home/landing-page-voices/male-zh-CN.mp3",
  },
};

const TranscriptDialog = (props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
}) => {
  return (
    <RadixDialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      title={"Support 50+ languages"}
      content={props.children}
      className={classNames.TranscriptDialogContent}
      contentClassName={classNames.TranscriptDialog}
      titleClassName={classNames.TranscriptDialogTitle}
    />
  );
};

function Video({
  playing,
  setPlaying,
  videoEl,
  audioRef,
  onTimeUpdate,
  overridePlay,
  handlePause,
  handlePlay,
  toLanguage,
}: {
  playing: boolean;
  setPlaying: (playing: boolean) => void;
  videoEl: React.RefObject<HTMLVideoElement>;
  audioRef: React.RefObject<HTMLAudioElement>;
  onTimeUpdate: (event: React.SyntheticEvent) => void;
  overridePlay?: () => void;
  handlePause: () => void;
  handlePlay: () => void;
  toLanguage: string;
  setToLanguage: React.Dispatch<React.SetStateAction<string>>;
}) {
  return (
    <div className={classNames.Video}>
      <video
        playsInline={true}
        ref={videoEl}
        controls={false}
        onPlaying={() => setPlaying(true)}
        onPlay={handlePlay}
        onPause={handlePause}
        onTimeUpdate={onTimeUpdate}
        onSeeking={onTimeUpdate}
        src="https://byrdhouse-public-prod.s3.us-west-1.amazonaws.com/static/home/landing-translation.mp4#t=0.01"
      />
      <audio
        ref={audioRef}
        onEnded={() => {
          // reload audio so it starts fresh next time
          audioRef.current?.load();
        }}
      />
      <button
        onClick={() => {
          if (overridePlay) {
            overridePlay();
          } else {
            if (videoEl.current) {
              videoEl.current.muted = toLanguage !== "de-DE";
              videoEl.current.play();
            }
          }
        }}
      >
        {!playing ? <IoPlay /> : undefined}
      </button>
    </div>
  );
}

function Transcript(props: {
  renderCaptions: { language: string; text: string }[];
  language: string;
}) {
  const captionsRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    captionsRef.current?.scrollTo({
      top: captionsRef.current.scrollHeight,
    });
  }, [captionsRef, props.renderCaptions]);

  return (
    <>
      <div className={classNames.TranscriptLanguage}>
        <p>
          {`${getLanguageFlag(props.language)} ${getLanguageName(
            props.language
          )}`}
        </p>
      </div>
      <div className={classNames.Transcript} ref={captionsRef}>
        {props.renderCaptions
          .filter((c) => c.language === props.language)
          .map((c, i) => (
            <div
              key={i}
              className={
                props.language === "de-DE"
                  ? classNames.Caption
                  : classNames.TranslatedCaption
              }
            >
              {c.text}
            </div>
          ))}
      </div>
    </>
  );
}

function TryTranslation() {
  const [playing, setPlaying] = useState(false);
  const videoEl = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  const isMobile = useIsMobile();
  const [mobileDialogOpen, setMobileDialogOpen] = useState(false);

  // Original / from language
  const fromLanguage = "de-DE";
  const [toLanguage, setToLanguage] = useState("en-US");

  useEffect(() => {
    if (!mobileDialogOpen) {
      setPlaying(false);
    }
  }, [mobileDialogOpen]);

  const [renderCaptions, setRenderCaptions] = useState<
    { language: string; text: string }[]
  >(transcript.slice(0, 6).flatMap((t) => t.captions));

  // Single combined time update function
  const handleTimeUpdate = useCallback(
    (event: React.SyntheticEvent) => {
      const video = videoEl.current;
      const audio = audioRef.current;
      if (!video || video.currentTime < 0.1) return;

      // 1. Switch male/female audio as needed
      // Only do this if the selected language is NOT the original (de-DE).
      if (toLanguage !== "de-DE" && audio) {
        const currentTime = video.currentTime;
        const isCurrentlyFemale = audio.src.includes("female");
        // Switch to male voice after 18s
        if (currentTime >= 18 && isCurrentlyFemale) {
          audio.src = audioTracks[toLanguage].male;
          audio.currentTime = currentTime - 18;
          if (!video.paused) {
            audio.play();
          }
        }
        // Switch back to female voice if user seeks below 18s
        else if (currentTime < 18 && !isCurrentlyFemale) {
          audio.src = audioTracks[toLanguage].female;
          audio.currentTime = currentTime;
          if (!video.paused) {
            audio.play();
          }
        }
      }

      // 2. Handle transcript updates
      const target = event.currentTarget as HTMLMediaElement;
      const currentTime = target.currentTime;

      const visibleTranscripts = transcript.filter(
        (t) => t.offset <= currentTime
      );

      // Combine all fully displayed captions
      const fullCaptions = visibleTranscripts
        .slice(0, -1)
        .flatMap((t) => t.captions);

      // Progressive reveal for the most recent caption
      const latestTranscript = visibleTranscripts.at(-1);
      const progressiveCaptions =
        latestTranscript?.captions.map((caption) => {
          const { language, text } = caption;
          const elapsed = currentTime - (latestTranscript?.offset ?? 0);

          // If video finished or at 0, show entire text
          if (
            video.paused &&
            (currentTime === video.duration || currentTime === 0)
          ) {
            return { language, text };
          }

          if (language === "zh-CN") {
            // Chinese: reveal character by character
            const characters = Array.from(text);
            const numToShow = 4 * Math.floor(elapsed);
            return {
              language,
              text: characters.slice(0, numToShow).join(""),
            };
          } else {
            // Other languages: reveal word by word
            const words = text.split(" ");
            const numToShow = 3 * Math.floor(elapsed);
            return {
              language,
              text: words.slice(0, numToShow).join(" "),
            };
          }
        }) ?? [];

      setRenderCaptions([...fullCaptions, ...progressiveCaptions]);
    },
    [toLanguage]
  );

  const handlePause = useCallback(() => {
    setPlaying(false);

    if (toLanguage === "de-DE") return;

    // Pause translated audio as well if the video isn't done
    if (
      audioRef.current &&
      videoEl.current?.currentTime !== videoEl.current?.duration
    ) {
      audioRef.current.pause();
    }
  }, [toLanguage]);

  const handlePlay = useCallback(() => {
    setPlaying(true);

    if (videoEl.current) {
      videoEl.current.muted = toLanguage !== "de-DE";
    }

    if (toLanguage === "de-DE") return;
    if (audioRef.current) {
      audioRef.current.play();
    }
  }, [toLanguage]);

  // Mute/unmute video whenever language changes
  useEffect(() => {
    const video = videoEl.current;
    const audio = audioRef.current;
    if (!video || !audio) return;

    if (toLanguage === "de-DE") {
      audio.pause();
      video.muted = false; // unmute for original
      return;
    }

    // Otherwise, mute original track
    video.muted = true;
    const currentTime = video.currentTime;
    const isAfter18s = currentTime >= 18;
    audio.src = audioTracks[toLanguage][isAfter18s ? "male" : "female"];
    audio.currentTime = isAfter18s ? currentTime - 18 : currentTime;

    if (!video.paused) {
      audio.play();
    }
  }, [toLanguage]);

  const languageSelects = useMemo(() => {
    return (
      <div className={classNames.LanguageSelects}>
        <Button color="white" backgroundColor="dark-gray" borderColor="black">
          {getLanguageName(fromLanguage)}
        </Button>
        <p>to</p>
        <Select
          color="black"
          backgroundColor="transparent"
          borderColor="black"
          selected={toLanguage}
          onChange={(value) => setToLanguage(value as string)}
          items={transcript[0].captions
            .map((caption) => caption.language)
            .map((language) => ({
              value: language,
              label: getLanguageName(language),
            }))}
        />
      </div>
    );
  }, [fromLanguage, toLanguage]);

  // Mobile mode
  if (isMobile) {
    return (
      <>
        <div className={classNames.TryTranslation}>
          <h3>Support 50+ languages</h3>
          {languageSelects}
          {/* Video */}
          <Video
            playing={playing}
            setPlaying={setPlaying}
            videoEl={videoEl}
            audioRef={audioRef}
            onTimeUpdate={handleTimeUpdate}
            overridePlay={() => {
              if (videoEl.current) {
                videoEl.current.muted = toLanguage !== "de-DE";
              }
              setMobileDialogOpen(true);
            }}
            toLanguage={toLanguage}
            setToLanguage={setToLanguage}
            handlePause={handlePause}
            handlePlay={handlePlay}
          />
          <Button
            onClick={() => {
              if (videoEl.current) {
                videoEl.current.muted = toLanguage !== "de-DE";
              }
              setMobileDialogOpen(true);
            }}
            color="white"
            backgroundColor="black"
            borderColor="black"
            className={classNames.StartTranslation}
          >
            Start translation
          </Button>
        </div>
        <TranscriptDialog
          open={mobileDialogOpen}
          onOpenChange={setMobileDialogOpen}
        >
          <Video
            playing={playing}
            setPlaying={setPlaying}
            videoEl={videoEl}
            audioRef={audioRef}
            onTimeUpdate={handleTimeUpdate}
            toLanguage={toLanguage}
            setToLanguage={setToLanguage}
            handlePause={handlePause}
            handlePlay={handlePlay}
          />
          <Transcript language={fromLanguage} renderCaptions={renderCaptions} />
          <Transcript language={toLanguage} renderCaptions={renderCaptions} />
        </TranscriptDialog>
      </>
    );
  }

  // Desktop mode
  return (
    <div className={classNames.TryTranslation}>
      <div className={classNames.Column}>
        <div className={classNames.Welcome}>
          <h3>Support 50+ languages</h3>
        </div>
        {/* Video */}
        <Video
          playing={playing}
          setPlaying={setPlaying}
          videoEl={videoEl}
          audioRef={audioRef}
          onTimeUpdate={handleTimeUpdate}
          toLanguage={toLanguage}
          setToLanguage={setToLanguage}
          handlePause={handlePause}
          handlePlay={handlePlay}
        />
      </div>
      <div className={classNames.Column}>
        {languageSelects}
        {/* Left transcript */}
        <Transcript language={fromLanguage} renderCaptions={renderCaptions} />
      </div>
      <div className={classNames.Column}>
        <Button
          onClick={() => {
            if (!videoEl.current) {
              return;
            }
            videoEl.current.muted = toLanguage !== "de-DE";

            if (videoEl.current.paused) {
              videoEl.current.play();
            } else {
              videoEl.current.pause();
            }
          }}
          color="white"
          backgroundColor="black"
          borderColor="black"
          className={classNames.StartTranslation}
        >
          {!playing ? "Start translation" : "Stop translation"}
        </Button>
        {/* Right transcript */}
        <Transcript language={toLanguage} renderCaptions={renderCaptions} />
      </div>
    </div>
  );
}

export default TryTranslation;
